/**
 * GalleryItemViewer — type-detect router for /gallery/:id.
 *
 * Loads item data from API (via list+find — TODO [07.6.alt]: add GET /api/gallery/:id
 * backend endpoint to replace list-find O(n) approach).
 *
 * Mounts <PhotoViewer /> for type='photo' (or legacy items without explicit type),
 * or <VideoViewer /> for type='video' (eager — VideoViewer landed in commit 50b7361).
 *
 * Route: /gallery/:id  → registered in App.jsx
 */
import { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PhotoViewer from './PhotoViewer';
import { Icon } from '../components/primitives';
import { useGalleryItemQuery } from '../core/queries/galleryItem';

import VideoViewer from './VideoViewer';

export default function GalleryItemViewer() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const backTarget = location.state?.from || '/gallery';
  const itemQuery = useGalleryItemQuery(id, { enabled: Boolean(id) });

  useEffect(() => {
    if (!id) {
      navigate(backTarget);
    }
  }, [id, navigate, backTarget]);

  const routedItem =
    location.state?.viewerItem && String(location.state.viewerItem.id) === String(id)
      ? location.state.viewerItem
      : null;
  const routedPhotoItems = Array.isArray(location.state?.viewerPhotoItems)
    ? location.state.viewerPhotoItems
    : [];
  const item = itemQuery.data?.item || routedItem || null;
  const photoItems = itemQuery.data?.photoItems?.length
    ? itemQuery.data.photoItems
    : routedPhotoItems;
  const error = !item && itemQuery.isError
    ? (itemQuery.error?.response?.status === 401 ? 'unauthorized' : 'server_error')
    : (!item && itemQuery.isSuccess ? 'not_found' : null);

  // ── Loading state ─────────────────────────────────────────
  if (itemQuery.isLoading && !item) {
    return (
      <div
        className="flex items-center justify-center bg-bg-app"
        style={{ minHeight: '100vh' }}
        data-testid="gallery-viewer-loading"
      >
        <Icon name="loader" size={32} className="text-text-secondary animate-spin" />
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────
  if (error || !item) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-4 bg-bg-app px-6 text-center"
        style={{ minHeight: '100vh' }}
        data-testid="gallery-viewer-error"
      >
        <Icon name="image" size={48} className="text-text-secondary" />
        <p className="text-text-primary font-sans font-bold" style={{ fontSize: 16 }}>
          {error === 'not_found' ? t('viewer.photoNotFound') : t('viewer.somethingWrong')}
        </p>
        <p className="text-text-secondary font-sans" style={{ fontSize: 14 }}>
          {error === 'not_found'
            ? t('viewer.photoMissing')
            : t('viewer.checkConnection')}
        </p>
        <button
          type="button"
          onClick={() => navigate(backTarget)}
          className="text-text-primary font-sans font-bold underline"
          style={{ fontSize: 14 }}
          data-testid="back-to-gallery-btn"
        >
          {t('viewer.backToGallery')}
        </button>
      </div>
    );
  }

  // ── Type routing ──────────────────────────────────────────
  if (item.type === 'video') {
    return <VideoViewer item={item} />;
  }

  // Default: photo type
  const currentPhotoIndex = photoItems.findIndex((g) => g.id === item.id);
  const previousPhoto = currentPhotoIndex > 0 ? photoItems[currentPhotoIndex - 1] : null;
  const nextPhoto =
    currentPhotoIndex >= 0 && currentPhotoIndex < photoItems.length - 1
      ? photoItems[currentPhotoIndex + 1]
      : null;
  const navigateToPhoto = (target, nextPhotoItems = photoItems) => {
    if (!target) return;
    navigate(`/gallery/${target.id}?type=photo`, {
      replace: true,
      state: {
        from: '/gallery',
        viewerItem: target,
        viewerPhotoItems: nextPhotoItems,
      },
    });
  };
  const handlePhotoDeleteSuccess = (deletedId) => {
    const remainingPhotoItems = photoItems.filter((photo) => String(photo.id) !== String(deletedId));
    const targetPhoto = nextPhoto || previousPhoto;
    if (targetPhoto) {
      navigateToPhoto(targetPhoto, remainingPhotoItems);
      return;
    }
    navigate(backTarget, { replace: true });
  };

  return (
    <PhotoViewer
      item={item}
      onSwipePrev={previousPhoto ? () => navigateToPhoto(previousPhoto) : null}
      onSwipeNext={nextPhoto ? () => navigateToPhoto(nextPhoto) : null}
      onDeleteSuccess={handlePhotoDeleteSuccess}
    />
  );
}
